import { useEffect, useRef } from 'react';
import SeoHead from '@/components/SEO/SeoHead';
import { useSubscription } from '@/utils/hooks/use-subscription';
import { coinflipStore, coinflipIntent } from '@/stores/coinflip.store';
import { useClientOnly } from '@/utils/hooks/use-client-only';
import { modalStore } from '@/stores/modal.store';
import { playSound } from '@/utils/playSound';
import { ApiRequest } from '@/utils/axios';
import { notify } from '@/utils/notify';
import SuccessIcon from '@/components/Notifications/SuccessIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import { inventoryStore } from '@/stores/inventory.store';
import userStore from '@/stores/user.store';
import { syncedNow } from '@/utils/timesync';
import { ICoinflipGame, ECoinflipSide } from '@/utils/types/ICoinflipGame';
import { socket } from '@/utils/hooks/use-subscription';
import AlertIcon from '@/components/Notifications/AlertIcon';
import CoinflipPage from '@/components/Coinflip/Game/CoinflipPage';

const Coinflip = () => {
    const {
        updateGame,
        releaseReservation,
        deleteGame,
        applyFinish,
        loadActiveGames,
        loadHistoryGames,
        clear,
    } = coinflipStore((state) => state);
    const { uncacheSteamInventory } = inventoryStore((state) => state);
    const { setTitle, setComponent, toggleModal, setData } = modalStore((state) => state);
    const sounds = modalStore((state) => state.sounds);
    const user = userStore((state) => state.user);

    const clientOnly = useClientOnly();

    const newAudio = useRef<HTMLAudioElement | null>(null);
    const soundsEnabled = useRef(sounds);

    // Keep a finished game on ACTIVE through its reveal, then move it to
    // History five seconds later. The server timestamp keeps every tab aligned.
    const finishScheduled = useRef<Set<number>>(new Set());
    function scheduleFinish(gameId: number) {
        if (finishScheduled.current.has(gameId)) return;
        const st = coinflipStore.getState();
        const game = st.games.get(gameId);
        if (!game) return;
        finishScheduled.current.add(gameId);
        st.markPending(gameId);
        const reveal = game.revealAt ?? syncedNow();
        const delay = Math.max(1500, reveal + 5000 - syncedNow());
        window.setTimeout(() => {
            coinflipStore.getState().moveToHistory(gameId);
            finishScheduled.current.delete(gameId);
        }, delay);
    }

    function openGame(game: ICoinflipGame) {
        setTitle(`COINFLIP #${game.id}`);
        setComponent('View coinflip');
        setData({ id: game.id });
        toggleModal(true);
    }

    useEffect(() => {
        soundsEnabled.current = sounds;
    }, [sounds]);

    useEffect(() => {
        loadActiveGames().then(() => {
            loadHistoryGames().then();
        });
        return clear();
    }, []);

    useEffect(() => {
        const reload = () => coinflipStore.getState().loadActiveGames().catch(() => undefined);
        socket.on('connect', reload);
        return () => {
            socket.off('connect', reload);
        };
    }, []);

    // Safety reconcile (independent of which socket event arrives): a finished
    // game (winner known) must not linger in ACTIVE, and a game whose timer
    // expired with an opponent but no winner likely missed its finish signal —
    // resolve it from the history endpoint. Guarantees finished games leave the
    // main list and land in History.
    useEffect(() => {
        const tick = () => {
            const st = coinflipStore.getState();
            const now = syncedNow();
            let poll = false;
            for (const g of st.activeGames()) {
                if (!g) continue;
                if (g.winnerSide) scheduleFinish(g.id);
                else if (g.secondUser && g.endsAt && now > g.endsAt + 5000) poll = true;
            }
            // A finished game whose finish signal we missed — pull the result;
            // the next tick sees its winnerSide and schedules it like the rest.
            if (poll) st.loadHistoryGames().then();
        };
        const iv = window.setInterval(tick, 2500);
        return () => window.clearInterval(iv);
    }, []);

    useSubscription('coinflipCreated', (game: ICoinflipGame) => {
        updateGame(game);
        // Only the tab that fired the create auto-opens the game — the
        // broadcast also reaches the user's other tabs, which must not switch
        // an already-open modal to this new game.
        if (game.firstUser.id === user.id && coinflipIntent.create) {
            coinflipIntent.create = false;
            openGame(game);
            notify(
                'success',
                <SuccessIcon />,
                <NotifyWrapper title="Success" text="You successfully created coinflip!" />
            );
        }
        playSound(newAudio, soundsEnabled.current);
    }).then();

    useSubscription('coinflipJoined', (game: ICoinflipGame) => {
        updateGame(game);
        const gameId = Number(game.id);
        const modal = modalStore.getState();
        const ownsTradeModal =
            modal.component === 'Steam trade' &&
            Number(modal.data?.coinflipGameId) === gameId;
        const joinedHere =
            Number(game.secondUser?.id) === Number(user.id) &&
            coinflipIntent.joinIds.has(gameId);

        if (ownsTradeModal || joinedHere) {
            coinflipIntent.joinIds.delete(gameId);
            openGame(game);
            notify(
                'success',
                <SuccessIcon />,
                <NotifyWrapper title="Success" text="You successfully joined coinflip!" />
            );
        }
    }).then();

    useSubscription('coinflipJoinReserved', (game: ICoinflipGame) => {
        updateGame(game);
    }).then();

    useSubscription(
        'coinflipJoinReleased',
        (payload: { gameId: number; reason: 'EXPIRED' | 'DECLINED' | 'FAILED' }) => {
            const gameId = Number(payload.gameId);
            const reservedUserId = coinflipStore.getState().games.get(gameId)?.pendingJoin?.user.id;
            releaseReservation(gameId);
            coinflipIntent.joinIds.delete(gameId);
            if (Number(reservedUserId) === Number(user.id)) {
                const modal = modalStore.getState();
                if (
                    modal.component === 'Steam trade' &&
                    Number(modal.data?.coinflipGameId) === gameId
                ) {
                    modal.close();
                }
                const reason = payload.reason === 'EXPIRED'
                    ? 'Trade confirmation time expired'
                    : 'Coinflip reservation was released';
                notify('error', <AlertIcon />, <NotifyWrapper title="Coinflip" text={reason} />);
            }
        }
    ).then();

    useSubscription('coinflipDeleted', (payload: number | { id: number }) => {
        const gameId = typeof payload === 'number' ? payload : payload.id;
        const game = coinflipStore.getState().games.get(gameId);
        if (game?.winnerSide) {
            // Finished game — already in the store with its winner/secret from
            // the coinflipFinished event; just make sure it's surfaced in
            // History and keep the reveal window (idempotent). No refetch.
            scheduleFinish(gameId);
        } else {
            // Cancelled before finishing — wipe everything.
            deleteGame(gameId);
        }
    }).then();

    useSubscription(
        'coinflipFinished',
        (payload: {
            gameId: number;
            winnerSide: ECoinflipSide;
            flipStartAt: number;
            revealAt: number;
            clearAt: number;
            hash?: string;
            secret?: string;
        }) => {
            const { gameId, winnerSide, flipStartAt, revealAt, clearAt, hash, secret } = payload;
            // Apply winner data + provably-fair fields straight from the socket
            // so the active row, the History list and the "View coinflip" modal
            // all have everything they need. This replaces the previous
            // per-finish `loadHistoryGames()` refetch of /coinflip/history/all,
            // which fired on every finish (incl. fake games) × every open tab.
            applyFinish(gameId, { winnerSide, flipStartAt, revealAt, clearAt, hash, secret });

            // Surface in HISTORY now, but keep it on the main list for 15s while
            // the row plays the spinning-avatar win animation, then drop it.
            scheduleFinish(gameId);
        }
    ).then();

    useSubscription('tradeReceived', async () => {
        const {
            data: {
                response: {
                    global: { appid },
                },
            },
        } = await ApiRequest.get('/settings/list');
        uncacheSteamInventory(appid, 2).then();
    }).then();

    return (
        <>
            <SeoHead category="Coinflip" path="/coinflip" />
            {clientOnly && <CoinflipPage />}
            <audio ref={newAudio} src="/audio/place_bet.mp3" />
        </>
    );
};

export default Coinflip;
